import React, { StrictMode, ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import App from './App.tsx';
import './index.css';
import { SessionContextProvider } from './contexts/SessionContext.tsx';
import { AuthProvider } from './hooks/useAuth.ts';
import { supabase } from './lib/supabase.ts';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      // Tela amigável: o usuário vê o que fazer; o detalhe técnico fica recolhido para o suporte.
      return (
        <div role="alert" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backgroundColor: '#f8fafc', fontFamily: 'system-ui, sans-serif' }}>
          <div style={{ maxWidth: '32rem', width: '100%', backgroundColor: '#fff', borderRadius: '1rem', padding: '2rem', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', textAlign: 'center' }}>
            <h1 style={{ color: '#1f2937', fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Algo deu errado nesta tela</h1>
            <p style={{ marginTop: '0.75rem', color: '#4b5563' }}>
              Recarregue a página para continuar. Se o erro voltar, envie o detalhe abaixo para o suporte.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{ marginTop: '1.25rem', backgroundColor: '#2563eb', color: '#fff', border: 0, borderRadius: '0.5rem', padding: '0.75rem 1.5rem', fontSize: '1rem', fontWeight: 600, cursor: 'pointer' }}
            >
              Recarregar a página
            </button>
            <details style={{ marginTop: '1.5rem', textAlign: 'left', color: '#6b7280', fontSize: '0.8rem' }}>
              <summary style={{ cursor: 'pointer' }}>Detalhes técnicos</summary>
              <pre style={{ marginTop: '0.5rem', padding: '0.75rem', backgroundColor: '#f3f4f6', borderRadius: '0.5rem', whiteSpace: 'pre-wrap', overflowX: 'auto' }}>
                {this.state.error?.toString()}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

const rootElement = document.getElementById('root')!;
const root = createRoot(rootElement);

if (!supabase) {
  root.render(
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl">⚠️</span>
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">Configuração Necessária</h1>
        <p className="text-gray-600 mb-6">
          As variáveis de ambiente do Supabase não foram encontradas.
        </p>
        <div className="text-left bg-gray-100 p-4 rounded-lg text-sm font-mono mb-6 overflow-x-auto">
          <p className="text-gray-500 mb-2">// Crie um arquivo .env na raiz do projeto:</p>
          <p className="text-blue-600">VITE_SUPABASE_URL=...</p>
          <p className="text-blue-600">VITE_SUPABASE_ANON_KEY=...</p>
        </div>
        <p className="text-sm text-gray-500">
          Consulte o arquivo SETUP_INSTRUCTIONS.md para mais detalhes.
        </p>
      </div>
    </div>
  );
} else {
  root.render(
    <StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <SessionContextProvider supabaseClient={supabase}>
            <AuthProvider>
              <App />
            </AuthProvider>
          </SessionContextProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </ErrorBoundary>
    </StrictMode>
  );
}